import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  AppBar,
  Toolbar,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Snackbar,
  Divider,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import appointmentService, { Appointment } from '../services/appointmentService';
import { toast } from 'react-toastify';
import { format, parseISO } from 'date-fns';

const AppointmentsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [hasMore, setHasMore] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [rescheduleDialog, setRescheduleDialog] = useState(false);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [notesDialog, setNotesDialog] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<any>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [appointmentForm, setAppointmentForm] = useState({
    providerId: '',
    date: '',
    time: '',
    message: ''
  });

  const handleLogout = () => {
    logout();
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  useEffect(() => {
    loadAppointments();
    loadProviders();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const response = await appointmentService.getMyAppointments();
      setAppointments(response.content);
      setCursor(response.nextCursor);
      setHasMore(response.hasNext);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  const loadProviders = async () => {
    try {
      const providers = await appointmentService.getAvailableProviders();
      setProviders(providers);
    } catch (error) {
      console.error('Error loading providers:', error);
    }
  };

  const handleReschedule = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setAppointmentForm({
      providerId: appointment.providerId.toString(),
      date: appointment.appointmentDate,
      time: appointment.timeSlot,
      message: appointment.message || ''
    });
    setRescheduleDialog(true);
  };

  const handleCancel = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setCancelDialog(true);
  };

  const handleViewNotes = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setNotesDialog(true);
  };

  const handleScheduleSubmit = async () => {
    try {
      await appointmentService.createAppointment({
        providerId: parseInt(appointmentForm.providerId),
        appointmentDate: appointmentForm.date,
        timeSlot: appointmentForm.time,
        message: appointmentForm.message
      });
      
      setOpenDialog(false);
      setAppointmentForm({ providerId: '', date: '', time: '', message: '' });
      toast.success('Appointment scheduled successfully!');
      loadAppointments();
    } catch (error) {
      console.error('Error scheduling appointment:', error);
      toast.error('Failed to schedule appointment');
    }
  };

  const handleRescheduleSubmit = async () => {
    if (!selectedAppointment) return;
    
    try {
      await appointmentService.rescheduleAppointment(selectedAppointment.id, {
        appointmentDate: appointmentForm.date,
        timeSlot: appointmentForm.time,
        message: appointmentForm.message
      });
      
      setRescheduleDialog(false);
      setSelectedAppointment(null);
      toast.success('Appointment rescheduled successfully!');
      loadAppointments();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast.error('Failed to reschedule appointment');
    }
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointment) return;
    
    try {
      await appointmentService.cancelAppointment(selectedAppointment.id, 'Cancelled by student');
      
      setCancelDialog(false);
      setSelectedAppointment(null);
      toast.success('Appointment cancelled successfully');
      loadAppointments();
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast.error('Failed to cancel appointment');
    }
  };

  const handleFormChange = (field: string, value: string) => {
    setAppointmentForm(prev => ({ ...prev, [field]: value }));
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'warning';
      case 'approved': return 'primary';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Psychology Profile System - Appointments
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {user?.firstName?.charAt(0) || 'U'}
            </Avatar>
            <Typography variant="body1">
              {user?.firstName} {user?.lastName}
            </Typography>
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button variant="outlined" onClick={handleBack}>
              ← Back to Dashboard
            </Button>
            <Typography variant="h4">
              My Appointments
            </Typography>
          </Box>
          <Button variant="contained" onClick={() => setOpenDialog(true)}>
            Schedule New Appointment
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (

          <Grid container spacing={3}>
            {appointments.map((appointment) => (
              <Grid item xs={12} md={6} key={appointment.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6">
                        Appointment #{appointment.id}
                      </Typography>
                      <Chip 
                        label={appointment.status} 
                        color={getStatusColor(appointment.status) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Date:</strong> {formatDate(appointment.appointmentDate)}
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      <strong>Time:</strong> {appointment.timeSlot}
                    </Typography>
                    
                    {appointment.provider && (
                      <Typography variant="body1" sx={{ mb: 1 }}>
                        <strong>Provider:</strong> Dr. {appointment.provider.firstName} {appointment.provider.lastName}
                      </Typography>
                    )}
                    
                    {appointment.message && (
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        <strong>Message:</strong> {appointment.message}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {(appointment.status === 'pending' || appointment.status === 'approved') && (
                        <>
                          <Button variant="outlined" size="small" onClick={() => handleReschedule(appointment)}>
                            Reschedule
                          </Button>
                          <Button variant="outlined" size="small" color="error" onClick={() => handleCancel(appointment)}>
                            Cancel
                          </Button>
                        </>
                      )}
                      {appointment.status === 'completed' && (
                        <Button variant="outlined" size="small" onClick={() => handleViewNotes(appointment)}>
                          View Details
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {appointments.length === 0 && (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" color="textSecondary">
              No appointments scheduled
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Schedule your first appointment to get started
            </Typography>
          </Box>
        )}

        {/* Schedule Appointment Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Schedule New Appointment</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                select
                label="Provider"
                fullWidth
                value={appointmentForm.providerId}
                onChange={(e) => handleFormChange('providerId', e.target.value)}
              >
                {providers.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    Dr. {provider.firstName} {provider.lastName}
                  </MenuItem>
                ))}
              </TextField>
              
              <TextField
                type="date"
                label="Preferred Date"
                fullWidth
                value={appointmentForm.date}
                onChange={(e) => handleFormChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                type="time"
                label="Preferred Time"
                fullWidth
                value={appointmentForm.time}
                onChange={(e) => handleFormChange('time', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="Message (Optional)"
                multiline
                rows={3}
                fullWidth
                value={appointmentForm.message}
                onChange={(e) => handleFormChange('message', e.target.value)}
                placeholder="Briefly describe why you'd like to schedule this appointment..."
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleScheduleSubmit}
              disabled={!appointmentForm.providerId || !appointmentForm.date || !appointmentForm.time}
            >
              Schedule Appointment
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reschedule Dialog */}
        <Dialog open={rescheduleDialog} onClose={() => setRescheduleDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reschedule Appointment</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                type="date"
                label="New Date"
                fullWidth
                value={appointmentForm.date}
                onChange={(e) => handleFormChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                type="time"
                label="New Time"
                fullWidth
                value={appointmentForm.time}
                onChange={(e) => handleFormChange('time', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              
              <TextField
                label="Message (Optional)"
                multiline
                rows={3}
                fullWidth
                value={appointmentForm.message}
                onChange={(e) => handleFormChange('message', e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRescheduleDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleRescheduleSubmit}
              disabled={!appointmentForm.date || !appointmentForm.time}
            >
              Reschedule
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Dialog */}
        <Dialog open={cancelDialog} onClose={() => setCancelDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Cancel Appointment</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to cancel this appointment?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialog(false)}>
              No, Keep It
            </Button>
            <Button 
              variant="contained" 
              color="error"
              onClick={handleCancelConfirm}
            >
              Yes, Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notes Dialog */}
        <Dialog open={notesDialog} onClose={() => setNotesDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Appointment Details</DialogTitle>
          <DialogContent>
            {selectedAppointment && (
              <Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Status:</strong> {selectedAppointment.status}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Date:</strong> {formatDate(selectedAppointment.appointmentDate)}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Time:</strong> {selectedAppointment.timeSlot}
                </Typography>
                {selectedAppointment.message && (
                  <Typography variant="body1">
                    <strong>Notes:</strong> {selectedAppointment.message}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotesDialog(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
        >
          <Alert severity="success">
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default AppointmentsPage;